// frontend/src/hooks/useVoiceAPI.js

import { useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../services/apiService';
import toast from 'react-hot-toast';

// Helper for stable dependencies
const useStableDependency = (value) => {
    const ref = useRef(value);
    if (JSON.stringify(value) !== JSON.stringify(ref.current)) {
        ref.current = value;
    }
    return ref.current;
};

// Hook for voice statistics
export const useVoiceStats = (profileId, days = 7, showTest = false) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const stableProfileId = useStableDependency(profileId);

    const fetchStats = useCallback(async () => {
        if (!stableProfileId) return;
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (days !== undefined && days !== null) params.append('days', days);
            if (showTest) params.append('show_test', 'true');
            const queryParam = params.toString() ? `?${params.toString()}` : '';
            const data = await apiService.makeRequest(`/voice/stats/${stableProfileId}${queryParam}`);
            setStats(data);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [stableProfileId, days, showTest]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    return { stats, loading, error, refetch: fetchStats };
};


// Hook for active calls
const globalLastFetch = {}; // specific cache to prevent loop floods

export const useActiveCalls = (profileId, showTest = false) => {
    const [activeCalls, setActiveCalls] = useState([]);
    const [loading, setLoading] = useState(true);
    const stableProfileId = useStableDependency(profileId);

    const fetchActiveCalls = useCallback(async (isManual = false) => {
        if (!stableProfileId) return;

        // Rate Limiting Guard: Prevent firing more than once per 2 seconds per profile
        // Unless it's a manual refresh (isManual = true)
        const now = Date.now();
        const lastTime = globalLastFetch[stableProfileId] || 0;

        if (!isManual && now - lastTime < 2000) {
            if (lastTime !== 0) return;
        }

        if (!isManual) {
            globalLastFetch[stableProfileId] = now;
        }

        try {
            const queryParam = showTest ? '?show_test=true' : '';
            const data = await apiService.makeRequest(`/voice/active-calls/${stableProfileId}${queryParam}`);
            setActiveCalls(data || []);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching active calls:', error);
            setLoading(false);
        }
    }, [stableProfileId, showTest]);

    // [FIX] Auto-polling enabled (every 3 seconds)
    useEffect(() => {
        fetchActiveCalls();
        const interval = setInterval(() => fetchActiveCalls(), 3000);
        return () => clearInterval(interval);
    }, [fetchActiveCalls]);

    const terminateCall = async (callId) => {
        try {
            await apiService.makeRequest(`/voice/calls/${callId}/end`, { method: 'POST' });
            fetchActiveCalls(true); // Immediate refresh
        } catch (error) {
            console.error('Error terminating call:', error);
        }
    };

    const transferCall = async (callId, phoneNumber) => {
        try {
            await apiService.makeRequest(`/voice/calls/${callId}/transfer`, {
                method: 'POST',
                body: JSON.stringify({ phoneNumber }),
            });
            fetchActiveCalls(true);
        } catch (error) {
            // 🔴 Отказ обязан ДОЙТИ ДО ОПЕРАТОРА (11.09.2026, 260911-2cf).
            // С белым списком номеров у перевода появился штатный отказ
            // («номер системе не известен»), а раньше ошибка молча уезжала в
            // console.error: оператор посреди живого разговора видел бы
            // НИЧЕГО и считал, что перевод идёт. Причина названа ручкой —
            // показываем именно её. Toaster смонтирован в App.js:226.
            console.error('Error transferring call:', error);
            toast.error((error && error.message) || 'Перевод не выполнен: причина не названа');
        }
    };

    return { activeCalls, loading, refetch: () => fetchActiveCalls(true), terminateCall, transferCall };
};

// Hook for call history
//
// 🔴 ТРИ СОСТОЯНИЯ, А НЕ ДВА (15.09.2026, quick-260915-d2d). До этой правки
//    отказ ручки уезжал в `console.error`, а `calls` оставался пустым массивом:
//    «журнал не удалось спросить» и «звонков не было» приходили к экрану ОДНИМ
//    И ТЕМ ЖЕ значением, и различить их было НЕЧЕМ. Это тот же род дефекта, из-за
//    которого 395 обращений Fitenko были выброшены молча: отказ измерителя ≠ «нет».
//    Поле `error` ДОБАВЛЕНО, существующие НЕ переименованы — единственный прежний
//    потребитель (`components/voice/CallHistoryTable.js:59`) берёт
//    `{ calls, loading, refetch }` и правки не требует.
export const useCallHistory = (profileId, filters, showTest = false) => {
    const [calls, setCalls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const stableProfileId = useStableDependency(profileId);
    const stableFilters = useStableDependency(filters);

    const fetchCalls = useCallback(async () => {
        if (!stableProfileId) return;
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (stableFilters?.search) params.append('search', stableFilters.search);
            if (stableFilters?.status && stableFilters.status !== 'all') params.append('status', stableFilters.status);
            if (stableFilters?.outcome && stableFilters.outcome !== 'all') params.append('outcome', stableFilters.outcome);
            if (showTest || stableFilters?.showTest) params.append('show_test', 'true');

            const data = await apiService.makeRequest(
                `/voice/calls/${stableProfileId}?${params.toString()}`
            );
            setCalls(Array.isArray(data) ? data : []);
            // Сбрасываем ТОЛЬКО на удачном ответе: иначе прошлый отказ жил бы
            // вечно, а новый успех был бы не виден.
            setError(null);
        } catch (err) {
            console.error('Error fetching call history:', err);
            // Причину называем ДОСЛОВНО. «Что-то пошло не так» — это второй
            // способ сказать «ничего», он снова сливает отказ с пустотой.
            setError((err && err.message) || 'ручка журнала звонков не ответила, причина не названа');
        } finally {
            setLoading(false);
        }
    }, [stableProfileId, stableFilters, showTest]);

    useEffect(() => {
        fetchCalls();
    }, [fetchCalls]);

    return { calls, loading, error, refetch: fetchCalls };
};

// Hook for voice configuration
export const useVoiceConfig = (profileId) => {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const stableProfileId = useStableDependency(profileId);

    const fetchConfig = useCallback(async () => {
        if (!stableProfileId) return;
        try {
            setLoading(true);
            const data = await apiService.makeRequest(`/voice/configs/by-profile/${stableProfileId}`);
            // Ensure we handle both array and object responses safely
            const configData = Array.isArray(data) ? data[0] : data;
            setConfig(configData);
        } catch (error) {
            console.error('Error fetching voice config:', error);
        } finally {
            setLoading(false);
        }
    }, [stableProfileId]);

    const updateConfig = async (configData) => {
        try {
            const data = await apiService.makeRequest('/voice/configs', {
                method: 'POST',
                body: JSON.stringify(configData),
            });
            // After update, set the new config logic to avoid double fetch
            const newConfig = Array.isArray(data) ? data[0] : data;
            setConfig(newConfig);
            return newConfig;
        } catch (error) {
            console.error('Error updating voice config:', error);
            throw error;
        }
    };

    useEffect(() => {
        fetchConfig();
    }, [fetchConfig]);

    return { config, loading, updateConfig, refetch: fetchConfig };
};

// Hook for analytics
export const useVoiceAnalytics = (profileId, dateRange) => {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const stableProfileId = useStableDependency(profileId);
    const stableDateRange = useStableDependency(dateRange);

    useEffect(() => {
        let isMounted = true;
        const fetchAnalytics = async () => {
            if (!stableProfileId) return;
            try {
                setLoading(true);
                const params = new URLSearchParams();
                if (stableDateRange?.from) params.append('from', stableDateRange.from);
                if (stableDateRange?.to) params.append('to', stableDateRange.to);

                const data = await apiService.makeRequest(
                    `/voice/analytics/${stableProfileId}?${params.toString()}`
                );
                if (isMounted) setAnalytics(data);
            } catch (error) {
                console.error('Error fetching analytics:', error);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchAnalytics();
        return () => { isMounted = false; };
    }, [stableProfileId, stableDateRange]);

    return { analytics, loading };
};

// [PROTOCOL v5.1] Multilingual Hooks
export const useVoiceAgents = () => {
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;
        const fetchAgents = async () => {
            try {
                const data = await apiService.makeRequest('/voice/agents');
                if (isMounted) setAgents(data);
            } catch (error) {
                console.error('Error fetching voice agents:', error);
            } finally {
                if (isMounted) setLoading(false);
            }
        };
        fetchAgents();
        return () => { isMounted = false; };
    }, []);

    return { agents, loading };
};

export const useAgentProfiles = (agentId) => {
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchProfiles = useCallback(async () => {
        if (!agentId) return;
        setLoading(true);
        try {
            const data = await apiService.makeRequest(`/voice/agents/${agentId}/profiles`);
            setProfiles(data);
        } catch (error) {
            console.error('Error fetching agent profiles:', error);
        } finally {
            setLoading(false);
        }
    }, [agentId]);

    useEffect(() => {
        fetchProfiles();
    }, [fetchProfiles]);

    return { profiles, loading, refetch: fetchProfiles };
};
